import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Product } from 'src/app/interfaces/products.interface';
import { environment } from 'src/environments/environment';

export interface SeoData {
  title: string;
  description: string;
  keywords?: string;
  image?: string;

  /** URL actual (puede incluir query params). */
  url?: string;

  /** Canonical explícito (normalmente sin query params). */
  canonicalUrl?: string;

  /** Control de indexación. */
  robots?: 'index,follow' | 'noindex,follow' | 'noindex,nofollow';

  /** OpenGraph type */
  ogType?: 'website' | 'product' | 'article';

  /** Si es la home o página principal */
  main?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly baseUrl: string = (environment.publicBaseUrl ?? 'https://www.calypso.la').replace(/\/$/, '');
  private readonly titleSuffix = ' | Calypso del Caribe';
  private readonly siteName = 'Calypso del Caribe';

  constructor(
    private readonly title: Title,
    private readonly meta: Meta,
    @Inject(DOCUMENT) private readonly doc: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) { }

  /**
   * Configura SEO para la página actual.
   * Idempotente: múltiples llamadas no duplican tags.
   */
  public setSEO(data: SeoData): void {
    const fullTitle = this.buildTitle(data.title, data.main);

    // Title
    this.title.setTitle(fullTitle);
    this.meta.updateTag({ name: 'title', content: fullTitle });
    this.meta.updateTag({ itemprop: 'name', content: fullTitle });
    this.meta.updateTag({ property: 'og:title', content: fullTitle });
    this.meta.updateTag({ property: 'twitter:title', content: fullTitle });

    // Robots
    this.meta.updateTag({ name: 'robots', content: data.robots ?? 'index,follow' });

    // Open Graph base
    this.meta.updateTag({ property: 'og:type', content: data.ogType ?? 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: this.siteName });

    // Twitter base (sin extras)
    this.meta.updateTag({ property: 'twitter:card', content: 'summary_large_image' });

    // Description
    const description = this.normalizeDescription(data.description);
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ itemprop: 'description', content: description });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ property: 'twitter:description', content: description });
    }

    // Keywords (opcional)
    if (data.keywords) {
      this.meta.updateTag({ name: 'keywords', content: data.keywords });
    }

    // Image
    if (data.image) {
      const imageAbs = this.toAbsoluteUrl(data.image);
      this.meta.updateTag({ property: 'og:image', content: imageAbs });
      this.meta.updateTag({ property: 'twitter:image', content: imageAbs });
    } else {
      // Opcional: podrías limpiar og:image si cambia de página y ya no hay imagen
      // this.meta.removeTag('property="og:image"');
      // this.meta.removeTag('property="twitter:image"');
    }

    // URL meta (puede incluir query)
    if (data.url) {
      const urlAbs = this.toAbsoluteUrl(data.url);
      this.meta.updateTag({ property: 'og:url', content: urlAbs });
      this.meta.updateTag({ property: 'twitter:url', content: urlAbs });
    }

    // Canonical (prioridad: canonicalUrl > url)
    const canonical = data.canonicalUrl ?? data.url;
    if (canonical) {
      this.setCanonical(this.toAbsoluteUrl(canonical));
    }

    // QA opcional (solo dev): valida H1/heading si quieres
    this.devAssertHeadings();
  }

  /**
   * SEO para producto: NO construye rutas internamente (evita acoplamiento).
   * El caller decide el path real (incluyendo :city si aplica).
   */
  public setProductInfo(product: Product, productPath: string): void {
    this.clearProductInfo();
    if (!product) return;

    this.setSEO({
      title: product.nombre,
      description: product.descripcion ?? '',
      image: product.imagen,
      url: productPath,
      canonicalUrl: productPath,
      robots: 'index,follow',
      ogType: 'product',
      main: false,
    });

    // OpenGraph product:* / product:* (mantengo tu estilo)
    if (product.marca) {
      this.meta.updateTag({ property: 'product:brand', content: String(product.marca) });
    }
    if (product.precio != null) {
      this.meta.updateTag({ property: 'product:price:amount', content: String(product.precio) });
      this.meta.updateTag({ property: 'product:price:currency', content: 'COP' });
    }
    this.meta.updateTag({ property: 'product:availability', content: 'in stock' });

    if (product.sku) {
      this.meta.updateTag({ property: 'product:retailer_item_id', content: String(product.sku) });
    }
    if (product.categoria) {
      this.meta.updateTag({ property: 'product:item_group_id', content: String(product.categoria) });
    }
  }

  public clearProductInfo(): void {
    this.meta.removeTag('property="product:brand"');
    this.meta.removeTag('property="product:price:amount"');
    this.meta.removeTag('property="product:price:currency"');
    this.meta.removeTag('property="product:availability"');
    this.meta.removeTag('property="product:retailer_item_id"');
    this.meta.removeTag('property="product:item_group_id"');
  }

  // -----------------------
  // Helpers
  // -----------------------

  private buildTitle(title: string, isMain?: boolean): string {
    // Mejor: el caller agrega la ciudad al title si aplica: "Guayaquil - Res natural"
    return isMain ? title : `${title}${this.titleSuffix}`;
  }

  private toAbsoluteUrl(value: string): string {
    if (/^https?:\/\//i.test(value)) return value;
    const path = value.startsWith('/') ? value : `/${value}`;
    return `${this.baseUrl}${path}`;
  }

  private setCanonical(urlAbs: string): void {
    let link = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', urlAbs);
  }

  private normalizeDescription(input: string): string {
    if (!input) return '';
    const text = input.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > 180 ? `${text.slice(0, 177)}...` : text;
  }

  // -----------------------
  // QA (opcional)
  // -----------------------

  /**
   * Ayuda a cumplir:
   * - un solo H1 por página
   * - jerarquía razonable de headings (H1–H3)
   *
   * Esto NO corrige HTML: solo avisa en consola (dev).
   */
  private devAssertHeadings(): void {
    // Solo en browser, y solo si no es prod
    if (!isPlatformBrowser(this.platformId)) return;
    if (environment.production) return;

    const h1 = this.doc.querySelectorAll('h1');
    if (h1.length !== 1) {
      // eslint-disable-next-line no-console
      console.warn(`[SEO] Se esperaba 1 <h1>, encontrados: ${h1.length}`);
    }

    // Chequeo simple de jerarquía (no perfecto, pero útil)
    const headings = Array.from(this.doc.querySelectorAll('h1,h2,h3')) as HTMLElement[];
    let lastLevel = 0;

    for (const el of headings) {
      const level = Number(el.tagName.replace('H', ''));
      if (lastLevel && level - lastLevel > 1) {
        // eslint-disable-next-line no-console
        console.warn(`[SEO] Salto de jerarquía: H${lastLevel} -> H${level}`, el);
        break;
      }
      lastLevel = level;
    }
  }
}
